import React, { useState, useEffect } from 'react';
import { GetAdminParticipants } from '../../../utilities/BL/Admin/Games/PluckingRooster';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

const formatAssetUrl = (url, fallback = '') => {
    if (!url) return fallback;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/img/') || url.startsWith('/static/') || url.startsWith('static/')) return url;
    const baseUrl = API_URL.replace(/\/api\/?$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const extractRankingList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.rankingList)) return raw.rankingList;
    if (Array.isArray(raw.data?.rankingList)) return raw.data.rankingList;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.payload?.rankingList)) return raw.payload.rankingList;
    if (Array.isArray(raw.payload)) return raw.payload;
    if (Array.isArray(raw.players)) return raw.players;
    if (Array.isArray(raw.items)) return raw.items;
    return [];
};

const RankingPluckingRooster = ({ data, customConfig, roomId, isActiveWomensMode }) => {
    const [rankingList, setRankingList] = useState([]);
    const effectiveRoomId = roomId || data?.roomId || new URLSearchParams(window.location.search).get('roomId');

    const loadInitialRanking = async () => {
        try {
            const res = await GetAdminParticipants(effectiveRoomId, '', 1, 10);
            if (res.success && Array.isArray(res.data)) {
                const sorted = [...res.data]
                    .sort((a, b) => (b.score || 0) - (a.score || 0))
                    .slice(0, 10);
                setRankingList(sorted);
            }
        } catch (err) {
            console.error('[RankingPluckingRooster] Error al cargar ranking inicial:', err);
        }
    };

    useEffect(() => {
        try {
            document.getElementById("root")?.classList.add("BodyComplete");
        } catch (e) {}
        loadInitialRanking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveRoomId]);

    useEffect(() => {
        if (!data) return;
        const extracted = extractRankingList(data);
        if (extracted.length > 0) {
            const sorted = [...extracted]
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .slice(0, 10);
            setRankingList(sorted);
        } else {
            loadInitialRanking();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const bgImage = customConfig?.screenBackground
        ? formatAssetUrl(customConfig.screenBackground)
        : (isActiveWomensMode ? '/img/page/backgrounds/Fondo-SelfieFan-Femenil.jpeg' : '/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg');

    const sponsorLogo = (customConfig?.screenSponsor || customConfig?.sponsorLogo)
        ? formatAssetUrl(customConfig.screenSponsor || customConfig.sponsorLogo)
        : "/img/page/xolos-five.png";

    return (
        <>
            {/* FONDO DINÁMICO DEL ESTADIO */}
            <div className='SelfieFan__Screen__QR__Background'>
                <img src={bgImage} className='SelfieFan__Screen__Selfie__Background__Image' alt="Fondo Estadio" />
            </div>

            {/* CONTENEDOR TOP 10 JUMBOTRON */}
            <div className="container-fluid min-vh-100 d-flex flex-column justify-content-between p-4 position-relative" style={{ zIndex: 10 }}>
                {/* Header Jumbotron */}
                <header className="d-flex align-items-center justify-content-between bg-dark bg-opacity-75 p-3 rounded-4 border border-warning shadow-lg mb-3">
                    <img src={sponsorLogo} alt="Sponsor" style={{ maxHeight: '70px', maxWidth: '200px', objectFit: 'contain' }} />
                    <div className="text-center flex-grow-1 mx-3">
                        <h1 className="fw-black text-warning text-uppercase mb-0 tracking-wider" style={{ fontSize: '2.5rem', textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>
                            🏆 TOP 10 RANKING 🏆
                        </h1>
                        <span className="text-white fw-bold text-uppercase fs-5">Pégale al Personaje</span>
                    </div>
                    <img src="/img/page/xolos-five.png" alt="Xolos Logo" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                </header>

                {/* Lista Leaderboard Top 10 */}
                <div className="flex-grow-1 d-flex flex-column justify-content-center my-2">
                    <div className="row g-2 justify-content-center">
                        {rankingList.slice(0, 10).map((player, index) => {
                            const isTop1 = index === 0;
                            const isTop2 = index === 1;
                            const isTop3 = index === 2;

                            const rankBadgeBg = isTop1 ? 'bg-warning text-dark' : isTop2 ? 'bg-secondary text-white' : isTop3 ? 'bg-danger text-white' : 'bg-dark text-white border border-light';
                            const itemBg = isTop1 ? 'rgba(255, 215, 0, 0.35)' : isTop2 ? 'rgba(192, 192, 192, 0.25)' : isTop3 ? 'rgba(205, 127, 50, 0.25)' : 'rgba(0, 0, 0, 0.7)';
                            const borderColor = isTop1 ? '#ffd700' : isTop2 ? '#c0c0c0' : isTop3 ? '#cd7f32' : 'rgba(255, 255, 255, 0.2)';

                            return (
                                <div key={player.id || index} className="col-12 col-md-10 col-lg-8">
                                    <div 
                                        className="d-flex align-items-center justify-content-between p-2 px-4 rounded-4 shadow-sm transition-all"
                                        style={{
                                            backgroundColor: itemBg,
                                            borderLeft: `8px solid ${borderColor}`,
                                            backdropFilter: 'blur(8px)',
                                            borderTop: '1px solid rgba(255,255,255,0.15)',
                                            borderBottom: '1px solid rgba(0,0,0,0.4)'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-3 overflow-hidden">
                                            <span className={`badge rounded-circle p-2 fs-5 fw-black d-flex align-items-center justify-content-center ${rankBadgeBg}`} style={{ width: '45px', height: '45px' }}>
                                                #{index + 1}
                                            </span>
                                            <span className="fw-bold text-white fs-4 text-truncate" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                                                {player.fullName || player.name || 'Aficionado Interaktia'}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            <span className="fs-3 fw-black text-warning" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.9)' }}>
                                                {player.score || 0} <small className="fs-6 text-white-50">PTS</small>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {rankingList.length === 0 && (
                            <div className="col-12 text-center py-5">
                                <div className="bg-dark bg-opacity-75 p-4 rounded-4 border border-secondary text-white d-inline-block">
                                    <p className="fs-4 mb-0 italic">Esperando a los primeros jugadores de la partida...</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Credits */}
                <footer className='SelfieFan__Screen__QR__Footer FooterCredits text-center text-white-50'>
                    Hecho con ♥ por Five Digital Minds | 2026
                </footer>
            </div>
        </>
    );
};

export { RankingPluckingRooster };